import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { companySettingsService } from '../../services/companySettingsService';
import { useCompany } from '../../context/CompanyContext';
import {
  BillItemLineFormat, BillPaperSize, CompanySettings, UpdateCompanySettingsRequest,
} from '../../types';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import '../Products/ProductCategoryEditPage.css';
import './BillPrintSettingsPage.css';
import { ArrowLeft, Save } from 'lucide-react';

/** Sample rows shown in the live preview. */
const SAMPLE_ITEMS = [
  { code: 'SKU-1001', name: 'Sample Product A', qty: 2, price: 450.0, total: 900.0 },
  { code: 'SKU-1002', name: 'Sample Product with a Longer Name B', qty: 1, price: 1250.5, total: 1250.5 },
  { code: 'SKU-1003', name: 'Sample Product C', qty: 3, price: 90.0, total: 270.0 },
];

/**
 * Bill Printing configuration: which company-profile values print on the bill
 * header, how line items are laid out (1 or 2 lines, which columns) and the
 * target paper (A4 or 80mm receipt roll). The values themselves (logo, name,
 * address…) are maintained in Company Settings — this page only controls the
 * printed layout, with a live preview.
 */
const BillPrintSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoUrl } = useCompany();

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    companySettingsService.getCompanySettings()
      .then((res) => { if (res.data.data) setSettings(res.data.data); })
      .catch((err: unknown) => setError(parseApiError(err, t('billPrint.failedToLoad')).message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof CompanySettings>(key: K, value: CompanySettings[K]) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!settings) return;
    setError(null);
    setMessage(null);
    setSaving(true);
    try {
      // Full round-trip: the update endpoint replaces the whole settings row,
      // so every profile field is carried through unchanged.
      const payload: UpdateCompanySettingsRequest = { ...settings };
      const res = await companySettingsService.updateCompanySettings(payload);
      if (res.data.data) setSettings(res.data.data);
      setMessage(t('billPrint.updateSuccess'));
    } catch (err: unknown) {
      setError(parseApiError(err, t('billPrint.failedToUpdate')).message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key: keyof CompanySettings, label: string) => (
    <div className="form-check" key={String(key)}>
      <input
        type="checkbox"
        className="form-check-input"
        id={`bill-${String(key)}`}
        checked={Boolean(settings?.[key])}
        onChange={(e) => set(key, e.target.checked as never)}
      />
      <label className="form-check-label" htmlFor={`bill-${String(key)}`}>
        {label}
      </label>
    </div>
  );

  const textLine = (key: 'billHeaderLine1' | 'billHeaderLine2' | 'billFooterLine1' | 'billFooterLine2', label: string, placeholder: string) => (
    <div className="form-group" key={key}>
      <label className="form-label">{label}</label>
      <input
        type="text"
        className="form-input"
        value={settings?.[key] ?? ''}
        onChange={(e) => set(key, e.target.value as never)}
        placeholder={placeholder}
        maxLength={80}
      />
    </div>
  );

  if (loading || !settings) {
    return (
      <Layout>
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" style={{ margin: '16px' }}>
            <strong>{t('common.error')}:</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} aria-label={t('common.close')}></button>
          </div>
        )}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  const twoLine = settings.billItemLineFormat === BillItemLineFormat.TWO_LINE;
  const receipt = settings.billPaperSize === BillPaperSize.POS_80;

  const addressLine = [
    settings.addressLine1, settings.addressLine2,
    [settings.city, settings.state, settings.postalCode].filter(Boolean).join(', '),
    settings.country,
  ].filter((v) => v && String(v).trim().length > 0);

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('billPrint.title')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {message && (
          <div className="alert alert-success alert-dismissible fade show">
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label={t('common.close')}></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} aria-label={t('common.close')}></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('billPrint.layoutSection')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.paperSize')}</label>
                    <div className="form-check">
                      <input
                        type="radio" className="form-check-input" id="paper-a4" name="paperSize"
                        checked={!receipt}
                        onChange={() => set('billPaperSize', BillPaperSize.A4)}
                      />
                      <label className="form-check-label" htmlFor="paper-a4">{t('billPrint.paperA4')}</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio" className="form-check-input" id="paper-pos" name="paperSize"
                        checked={receipt}
                        onChange={() => set('billPaperSize', BillPaperSize.POS_80)}
                      />
                      <label className="form-check-label" htmlFor="paper-pos">{t('billPrint.paperPos80')}</label>
                    </div>
                    <small className="form-helper">{t('billPrint.paperSizeHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.headerSection')}</label>
                    {toggle('billShowLogo', t('billPrint.showLogo'))}
                    {toggle('billShowCompanyName', t('billPrint.showCompanyName'))}
                    {toggle('billShowAddress', t('billPrint.showAddress'))}
                    {toggle('billShowPhone', t('billPrint.showPhone'))}
                    {toggle('billShowEmail', t('billPrint.showEmail'))}
                    {toggle('billShowWebsite', t('billPrint.showWebsite'))}
                    <small className="form-helper">{t('billPrint.headerHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.headerWording')}</label>
                    {textLine('billHeaderLine1', t('billPrint.headerLine1'), t('billPrint.headerLine1Placeholder'))}
                    {textLine('billHeaderLine2', t('billPrint.headerLine2'), t('billPrint.headerLine2Placeholder'))}
                    <small className="form-helper">{t('billPrint.headerLinesHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.itemFormat')}</label>
                    <div className="form-check">
                      <input
                        type="radio" className="form-check-input" id="fmt-one" name="itemFormat"
                        checked={!twoLine}
                        onChange={() => set('billItemLineFormat', BillItemLineFormat.ONE_LINE)}
                      />
                      <label className="form-check-label" htmlFor="fmt-one">{t('billPrint.oneLine')}</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio" className="form-check-input" id="fmt-two" name="itemFormat"
                        checked={twoLine}
                        onChange={() => set('billItemLineFormat', BillItemLineFormat.TWO_LINE)}
                      />
                      <label className="form-check-label" htmlFor="fmt-two">{t('billPrint.twoLine')}</label>
                    </div>
                    <small className="form-helper">{t('billPrint.itemFormatHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.itemFields')}</label>
                    {toggle('billShowItemCode', t('billPrint.showItemCode'))}
                    {toggle('billShowQty', t('billPrint.showQty'))}
                    {toggle('billShowUnitPrice', t('billPrint.showUnitPrice'))}
                    {toggle('billShowLineTotal', t('billPrint.showLineTotal'))}
                    <small className="form-helper">{t('billPrint.itemFieldsHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.footerWording')}</label>
                    {textLine('billFooterLine1', t('billPrint.footerLine1'), t('billPrint.footerLine1Placeholder'))}
                    {textLine('billFooterLine2', t('billPrint.footerLine2'), t('billPrint.footerLine2Placeholder'))}
                    <small className="form-helper">{t('billPrint.footerLinesHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('billPrint.otherSection')}</label>
                    {toggle('billShowPaymentMethod', t('billPrint.showPaymentMethod'))}
                    {toggle('billShowTenderedAmount', t('billPrint.showTenderedAmount'))}
                    <small className="form-helper">{t('billPrint.otherHint')}</small>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('billPrint.preview')}</h3>
              </div>
              <div className="edit-card-body">
                <div className={`bill-preview ${receipt ? 'bill-preview-receipt' : 'bill-preview-a4'}`}>
                  {/* Header */}
                  {settings.billShowLogo && logoUrl && (
                    <img src={logoUrl} alt="" className="bill-preview-logo" />
                  )}
                  {settings.billShowCompanyName && (
                    <div className="bill-preview-name">{settings.companyName || t('billPrint.sampleCompany')}</div>
                  )}
                  {settings.billShowAddress && (
                    addressLine.length > 0
                      ? addressLine.map((l, i) => <div className="bill-preview-muted" key={i}>{l}</div>)
                      : <div className="bill-preview-muted">{t('billPrint.sampleAddress')}</div>
                  )}
                  {settings.billShowPhone && (
                    <div className="bill-preview-muted">Tel: {settings.phone || '011-2345678'}</div>
                  )}
                  {settings.billShowEmail && (
                    <div className="bill-preview-muted">{settings.email || 'info@example.com'}</div>
                  )}
                  {settings.billShowWebsite && (
                    <div className="bill-preview-muted">{settings.website || 'www.example.com'}</div>
                  )}
                  {settings.billHeaderLine1 && (
                    <div className="bill-preview-accent">{settings.billHeaderLine1}</div>
                  )}
                  {settings.billHeaderLine2 && (
                    <div className="bill-preview-accent">{settings.billHeaderLine2}</div>
                  )}

                  <div className="bill-preview-divider" />
                  <div className="bill-preview-title">SALES INVOICE · INV-000123</div>
                  <div className="bill-preview-divider" />

                  {/* Items */}
                  {twoLine ? (
                    SAMPLE_ITEMS.map((item) => (
                      <div className="bill-preview-item" key={item.code}>
                        <div className="bill-preview-item-name">
                          {settings.billShowItemCode ? `${item.code}  ` : ''}{item.name}
                        </div>
                        <div className="bill-preview-item-line2">
                          <span className="bill-preview-muted">
                            {settings.billShowQty && settings.billShowUnitPrice && `${item.qty} x ${item.price.toFixed(2)}`}
                            {settings.billShowQty && !settings.billShowUnitPrice && `${item.qty}`}
                            {!settings.billShowQty && settings.billShowUnitPrice && item.price.toFixed(2)}
                          </span>
                          {settings.billShowLineTotal && <strong>{item.total.toFixed(2)}</strong>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <table className="bill-preview-table">
                      <thead>
                        <tr>
                          {settings.billShowItemCode && <th>{t('billPrint.colCode')}</th>}
                          <th className="bill-preview-col-name">{t('billPrint.colName')}</th>
                          {settings.billShowQty && <th className="num">{t('billPrint.colQty')}</th>}
                          {settings.billShowUnitPrice && <th className="num">{t('billPrint.colPrice')}</th>}
                          {settings.billShowLineTotal && <th className="num">{t('billPrint.colTotal')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {SAMPLE_ITEMS.map((item) => (
                          <tr key={item.code}>
                            {settings.billShowItemCode && <td>{item.code}</td>}
                            <td className="bill-preview-col-name">{item.name}</td>
                            {settings.billShowQty && <td className="num">{item.qty}</td>}
                            {settings.billShowUnitPrice && <td className="num">{item.price.toFixed(2)}</td>}
                            {settings.billShowLineTotal && <td className="num"><strong>{item.total.toFixed(2)}</strong></td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div className="bill-preview-divider" />
                  <div className="bill-preview-total">
                    <span>Total</span>
                    <strong>2,420.50</strong>
                  </div>
                  {settings.billShowPaymentMethod && (
                    <div className="bill-preview-item-line2">
                      <span className="bill-preview-muted">Cash</span>
                      <span className="bill-preview-muted">2,420.50</span>
                    </div>
                  )}

                  {/* Footer */}
                  {(settings.billFooterLine1 || settings.billFooterLine2) && <div className="bill-preview-divider" />}
                  {settings.billFooterLine1 && <div className="bill-preview-accent">{settings.billFooterLine1}</div>}
                  {settings.billFooterLine2 && <div className="bill-preview-muted">{settings.billFooterLine2}</div>}
                </div>
                <p className="form-helper" style={{ marginTop: '10px' }}>
                  {t('billPrint.previewHint')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BillPrintSettingsPage;
